import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import Home from "@/pages/Home";
import ListingDetail from "@/pages/ListingDetail";
import CreateListing from "@/pages/CreateListing";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import MyListings from "@/pages/MyListings";
import AdminPanel from "@/pages/AdminPanel";
import Legal from "@/pages/Legal";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true, syncTouch: false });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

export const PageLoading = () => (
  <div className="grid min-h-[70vh] place-items-center">
    <Loader2 className="animate-spin text-coral" size={36} />
  </div>
);

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <PageLoading />;
  if (!user) return <Navigate to="/giris" state={{ from: loc.pathname }} replace />;
  return children;
}

function StaffProtected({ children }) {
  const { user } = useAuth();
  if (user === null) return <PageLoading />;
  if (!user) return <Navigate to="/giris" replace />;
  if (!["employee", "admin"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppShell() {
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/ilan/:id" element={<ListingDetail />} />
          <Route path="/ilan-ver" element={<Protected><CreateListing /></Protected>} />
          <Route path="/ilan-ver/edit/:id" element={<Protected><CreateListing /></Protected>} />
          <Route path="/giris" element={<Auth mode="login" />} />
          <Route path="/kayit" element={<Auth mode="register" />} />
          <Route path="/sifremi-unuttum" element={<Auth mode="forgot" />} />
          <Route path="/sifre-sifirla" element={<ResetPassword />} />
          <Route path="/hesabim" element={<Protected><MyListings /></Protected>} />
          <Route path="/panel" element={<StaffProtected><AdminPanel /></StaffProtected>} />
          <Route path="/kvkk-aydinlatma-metni" element={<Legal doc="kvkk" />} />
          <Route path="/kullanim-kosullari" element={<Legal doc="terms" />} />
          <Route path="/cerez-politikasi" element={<Legal doc="cookies" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <CookieConsent />
      <div className="h-16 md:hidden" />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <ScrollToTop />
            <SmoothScroll />
            <AppShell />
          </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
